import cn from 'classnames';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import ReCAPTCHA from 'react-google-recaptcha';
import { Country } from 'react-phone-number-input';
import useSWR, { preload } from 'swr';

import { api, fetcher } from '@/api';
import usePublicTranslation from '@/hooks/usePublicTranslation';
import { FormStructure } from '@/types/form';
import { secureDecode } from '@/utils/crypto';

import ErrorIcon from './ErrorIcon';
import PhoneField from './PhoneField';
import styles from './styles.module.scss';
import SuccessIcon from './SuccessIcon';

type Props = {
  name: string;
  errorText?: string;
  successText?: string;
};

export const preloadForm = (name: string) => {
  return preload(`/data/structure/form/public/by-source-type/${name}`, fetcher);
};

const Form: React.FC<Props> = ({ name, errorText, successText }) => {
  const t = usePublicTranslation();
  const formContainerRef = useRef<HTMLDivElement>(null);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [formError, setFormError] = useState<boolean>(false);

  const { data } = useSWR<FormStructure>(`/data/structure/form/public/by-source-type/${name}`, fetcher);

  const injectGoogleRecaptchaV2 = () => {
    const reCaptcha = formContainerRef.current?.querySelector('.g-recaptcha');
    if (!reCaptcha) return;

    const siteKey = reCaptcha.getAttribute('data-sitekey');
    if (!siteKey) return;

    let decodedSiteKey = null;

    try {
      decodedSiteKey = secureDecode(siteKey);
    } catch (error) {
      console.error('Failed to decode Google reCAPTCHA v2 site key', error);

      return;
    }

    const reCaptchaParent = reCaptcha.parentElement;
    if (reCaptchaParent) {
      const container = document.createElement('div');
      container.className = 'g-recaptcha-container';
      reCaptchaParent.insertBefore(container, reCaptcha);

      const root = createRoot(container);
      root.render(<ReCAPTCHA sitekey={decodedSiteKey} />);

      reCaptchaParent.removeChild(reCaptcha);
    }
  };

  const injectPhoneInputs = () => {
    const inputs = formContainerRef.current?.querySelectorAll<HTMLInputElement>('input[type="tel"]');
    inputs?.forEach(input => {
      const parent = input.parentElement;
      if (!parent) return;

      const countries = (input.dataset.phoneCountries ?? '')
        .split(',')
        .map(c => c.trim().toUpperCase())
        .filter(Boolean) as Country[];
      const defaultCountry = (input.dataset.phoneDefaultCountry?.toUpperCase() || undefined) as Country | undefined;
      const international = input.dataset.phoneInternational !== 'false';

      const container = document.createElement('div');
      container.className = 'phone-input-container';
      parent.insertBefore(container, input);

      // The original input stays in the form (hidden) so its name/value are submitted as E.164.
      input.type = 'hidden';

      createRoot(container).render(
        <PhoneField
          source={input}
          countries={countries}
          defaultCountry={defaultCountry}
          international={international}
        />
      );
    });
  };

  const handleFormSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();

      const form = event.target as HTMLFormElement;
      const formData = new FormData(form);

      const data: Record<string, unknown> = {};

      formData.forEach((value, key) => {
        data[key] = value;
      });

      api
        .post(`/submission/public/${name}`, data)
        .then(() => {
          form.reset();
          setFormSubmitted(true);
          setFormError(false);
        })
        .catch(() => {
          setFormError(true);
          setFormSubmitted(false);
        });
    },
    [name]
  );

  useEffect(() => {
    injectGoogleRecaptchaV2();
    injectPhoneInputs();
  }, [data?.body]);

  return (
    <div ref={formContainerRef} onSubmitCapture={handleFormSubmit} className={cn('wp-block-erom-form', `${name}`)}>
      <div dangerouslySetInnerHTML={{ __html: data?.body || '' }} />
      {formError && (
        <div className={cn(styles.feedbackMessage, styles.error, 'feedback-message', 'error')}>
          <div className={cn(styles.feedbackMessageText, 'feedback-message-text')}>
            <ErrorIcon />
            {errorText || t('forms.error')}
          </div>
        </div>
      )}
      {formSubmitted && (
        <div className={cn(styles.feedbackMessage, styles.success, 'feedback-message', 'success')}>
          <div className={cn(styles.feedbackMessageText, 'feedback-message-text')}>
            <SuccessIcon />
            {successText || t('forms.success')}
          </div>
        </div>
      )}
    </div>
  );
};

export default Form;
